import { JBAction } from './actions'
import { createRemoveDialog, createUserMessage } from '../../views/apps/engagement/marketing-automation/journey-builder/views/canvas/plugins'
import { NodeType } from '../../views/apps/engagement/marketing-automation/journey-builder/core/workflow'

// List of actions that require validation (popup display) before execution
const actionsToValidate = [
  JBAction.LINK_REMOVED,
  JBAction.NODE_REMOVED,
  JBAction.REMOVE_SELECTION,
  JBAction.CUT_SELECTION
]

/**
 * Middleware to validate and handle specific actions related to removing elements
 * in the workflow. It ensures that certain conditions are met before proceeding
 * with the action and displays dialogs or messages when necessary.
 * @type {function(MiddlewareAPI): function(Dispatch<UnknownAction>): function(UnknownAction): UnknownAction}
 */
export const RemoveElementValidationMiddleware =
  ({ getState }) =>
    next =>
      action => {
        if (!getState || typeof getState !== 'function') {
          return
        }

        const workflowState = getState().workflow

        // Skip validation for actions not in the list
        if (!actionsToValidate.includes(action.type)) {
          return next(action)
        }

        // Handle link removal action
        if (action.type === JBAction.LINK_REMOVED) {
          handleRemoveLink(next, action, action.payload, workflowState)
        }

        // Handle node removal action
        if (action.type === JBAction.NODE_REMOVED) {
          handleRemoveNode(next, action, action.payload, workflowState)
        }

        // Handle selection removal action
        if (action.type === JBAction.REMOVE_SELECTION || action.type === JBAction.CUT_SELECTION) {
          const { nodes = [], links = [] } = getState().ui.selection || {}

          const bulkDeletion = action.meta?.bulkDeletion ?? false
          if (bulkDeletion) {
            return next({
              ...action,
              meta: {
                ...action.meta,
                bulkDeletion: true,
                removeCaseCondition: true
              }
            })
          }

          // Handle case when only one link is selected
          if (links.length === 1 && nodes.length === 0) {
            handleRemoveLink(next, action, links[0], workflowState)
            return
          }

          // Handle case when only one node is selected
          if (links.length === 0 && nodes.length === 1) {
            handleRemoveNode(next, action, nodes[0], workflowState)
            return
          }

          // !NOTE: Not dead code, will be used in future when multi nodes selection remove validation is required
          // Check if selected nodes are connected to a CASE or IF_ELSE node as targets
          // uncomment below code for multi nodes remove validation,
          // to prevent deletion if any selected node is target of case/if-else node
          // const nodeCaseSources = nodes.filter(nodeId => {
          //   return !!Object.values(workflowState.links).filter(
          //     l => l.target === nodeId &&
          //       (workflowState.nodes[l.source].type === NodeType.CASE ||
          //         workflowState.nodes[l.source].type === NodeType.IF_ELSE)
          //   ).length
          // })

          // if (nodeCaseSources.length) {
          //   createUserMessage(popupMessage.multiNodesRemove)
          //   return
          // }

          // Check if selected links originate from a CASE or IF_ELSE node
          // const linkCaseSources = links.filter(l => {
          //   const linkData = workflowState.links[l]
          //   const sourceNode = workflowState.nodes[linkData.source]
          //   return sourceNode.type === NodeType.CASE || sourceNode.type === NodeType.IF_ELSE
          // })

          // if (linkCaseSources.length) {
          //   createUserMessage(popupMessage.multiLinksRemove)
          //   return
          // }
          // ---------------------------------------

          // Proceed with the action if no validation issues are found
          return next(action)
        }
      }/**
 * Opens a confirmation dialog for the user to decide whether to update
 * the condition list of a source node.
 * - Displays a dialog with the provided content.
 * - Executes a callback based on the user's decision.
 * - Optionally prevents proceeding if the user does not choose "Yes".
 *
 * @param {Dispatch<UnknownAction>} next - The next middleware function in the chain.
 * @param {UnknownAction} action - The Redux action being processed.
 * @param {string} dialogContent - The content to display in the dialog.
 * @param {boolean} [proceedOnlyWhenChooseYes=false] - If true, prevents proceeding unless the user chooses "Yes".
 */
const openDialog = (next, action, dialogContent, proceedOnlyWhenChooseYes = false) => {
  // display dialog to confirm update source node condition list
  const { type, payload } = action

  // Callback to handle the user's decision
  /** @type {(updateCaseNode: boolean) => void} */
  const dialogCloseCb = removeCaseCondition => {
    if (!removeCaseCondition && proceedOnlyWhenChooseYes) {
      return
    }
    return next({ type, payload, meta: { removeCaseCondition } })
  }

  // Create and display the dialog
  createRemoveDialog(dialogCloseCb, dialogContent)
}

/**
 * Handles the removal of a link, displaying a confirmation dialog if the link
 * originates from a CASE or IF_ELSE node.
 *
 * - Checks if the link originates from a CASE/IF_ELSE node and whether the target is a temporary node.
 * - Displays appropriate user messages or confirmation dialogs based on the link's context.
 * - Proceeds with the action if no validation issues are found.
 *
 * @param {Dispatch<UnknownAction>} next - The next middleware function in the chain.
 * @param {UnknownAction} action - The Redux action being processed.
 * @param {string} linkId - The ID of the link being removed.
 * @param {WorkflowState} workflowState - The current state of the workflow.
 */
const handleRemoveLink = (next, action, linkId, workflowState) => {
  // check if selected link is outgoing link from case/if-else node and display popup when
  const linkData = workflowState.links[linkId]

  // If link doesn't exist in workflow state (e.g., was only created locally), just proceed
  if (!linkData) {
    return next(action)
  }

  const sourceNode = workflowState.nodes[linkData.source]

  // If source node doesn't exist, just proceed
  if (!sourceNode) {
    return next(action)
  }

  const targetNode = workflowState.nodes[linkData.target]
  const isSourceCaseNode = sourceNode.type === NodeType.CASE
  const isSourceIfElseNode = sourceNode.type === NodeType.IF_ELSE
  const isTargetTemporaryNode = targetNode?.type === NodeType.TEMPORARY

  // Handle CASE node specific logic
  if (isSourceCaseNode) {
    // Display a message if the link is the default target of a CASE node and connects to a temporary node
    if (sourceNode.data.default_target === linkData.target && isTargetTemporaryNode) {
      createUserMessage(popupMessage.temporaryDefaultTargetLinkRemove)
      return
    }

    // Proceed with the action if the link is the default target connecting to a non-temporary node
    if (sourceNode.data.default_target === linkData.target && !isTargetTemporaryNode) {
      return next(action)
    }

    const content = isTargetTemporaryNode ? popupMessage.temporaryLinkRemove : popupMessage.linkRemove
    openDialog(next, action, content, isTargetTemporaryNode)
    return
  }

  // Handle IF_ELSE node specific logic
  if (isSourceIfElseNode) {
    // Check if this is the else_target link
    const isElseLink = linkData.metadata?.isElse === true ||
      linkData.label === 'else' ||
      sourceNode.data.else_target === linkData.target

    // Display a message if the link is the else target and connects to a temporary node
    if (isElseLink && isTargetTemporaryNode) {
      createUserMessage(popupMessage.temporaryElseTargetLinkRemove)
      return
    }

    // For else links with non-temporary targets, allow removal - it will create new else with temp node
    if (isElseLink && !isTargetTemporaryNode) {
      return next(action)
    }

    // For condition links with temporary targets, auto-proceed with removal
    if (isTargetTemporaryNode) {
      return next({ ...action, meta: { ...action.meta, removeCaseCondition: true } })
    }

    // For condition links with non-temporary targets, show dialog
    const content = popupMessage.ifElseLinkRemove
    openDialog(next, action, content, false)
    return
  }  // Proceed with the action if the link does not originate from a CASE or IF_ELSE node
  return next(action)
}/**
 * Handles the removal of a node, displaying a confirmation dialog if the node
 * is a target of a CASE or IF_ELSE node.
 *
 * - Checks if the node is temporary and connected to a CASE/IF_ELSE node as its default/else target.
 * - Displays appropriate user messages or confirmation dialogs based on the node's context.
 * - Proceeds with the action if the node is not connected to a CASE/IF_ELSE node.
 *
 * @param {Dispatch<UnknownAction>} next - The next middleware function in the chain.
 * @param {UnknownAction} action - The Redux action being processed.
 * @param {string} nodeId - The ID of the node being removed.
 * @param {WorkflowState} workflowState - The current state of the workflow.
 */
const handleRemoveNode = (next, action, nodeId, workflowState) => {
  const nodeToRemove = workflowState.nodes[nodeId]

  if (!nodeToRemove) {
    return next(action)
  }

  const isNodeTemporary = nodeToRemove.type === NodeType.TEMPORARY

  // check if selected node as target is connected to case/if-else node and display popup
  const incomingLinks = Object.values(workflowState.links).filter(l => l.target === nodeId)
  const sources = incomingLinks.map(l => workflowState.nodes[l.source])

  // Check for CASE node connections (condition targets, not default_target)
  const caseSource = sources.find(s => s?.type === NodeType.CASE && s.data.default_target !== nodeId)

  // Check for IF_ELSE node connections (condition targets, not else_target)
  const ifElseSource = sources.find(s => s?.type === NodeType.IF_ELSE && s.data.else_target !== nodeId)

  if (!caseSource && !ifElseSource) {
    // If the node is not a target of a CASE or IF_ELSE node condition, proceed with the action
    return next(action)
  }

  const content = isNodeTemporary ? popupMessage.temporaryNodeRemove : popupMessage.nodeRemove

  // Display a dialog to confirm updating the condition list
  openDialog(next, action, content, isNodeTemporary)
}

const popupMessage = {
  temporaryNodeRemove:
    'Removing this temporary node will also remove its condition from the CASE/IF-ELSE node. Do you want to continue?',
  nodeRemove: 'Selected node is connected to CASE/IF-ELSE node. Should the condition list be updated for source node?',
  temporaryDefaultTargetLinkRemove:
    'This link cannot be removed because it\'s connected to the temporary default target of a CASE node.',
  temporaryElseTargetLinkRemove:
    'This link cannot be removed because it\'s connected to the temporary else target of an IF-ELSE node.',
  temporaryDefaultTargetRemove:
    'The temporary node cannot be removed because it is set as the default target for a CASE node.',
  temporaryLinkRemove:
    'Removing a link connected to a temporary node will remove the related condition from the CASE node. Do you want to continue?',
  linkRemove: 'Selected link is connected to CASE node. Should the condition list be updated for source node?',
  ifElseLinkRemove: 'Selected link is connected to IF-ELSE node. Should the condition list be updated for source node?',
  multiLinksRemove:
    'The outgoing links from a CASE/IF-ELSE node cannot be removed when deleting multiple elements. Please remove it individually.',
  multiNodesRemove:
    'The condition target of a CASE/IF-ELSE node cannot be removed when deleting multiple elements. Please remove it individually.'
}
